import {
  ADMIN_CHOICE,
  DOMAIN_CHOICE,
  DUPLICATE_KIND,
  DUPLICATE_RESOLUTION,
  INVITE_MODE,
} from '../../../domains/people-access/constants/inviteModes.js';
import { IMPORT_TARGET } from '../../../domains/people-access/constants/importTargets.js';
import { IMPORT_ACTIONS } from '../../../domains/people-access/constants/importWizardActions.js';
import {
  IMPORT_STEP_ORDER,
  IMPORT_STEPS,
} from '../../../domains/people-access/constants/importSteps.js';
import { ROW_STATUS } from '../../../domains/people-access/constants/reviewFilters.js';
import { accessFromFile, normalizeAccess } from '../../../domains/people-access/utils/accessRules.js';
import { automapColumns } from '../../../domains/people-access/utils/automapColumns.js';
import { buildRows } from '../../../domains/people-access/utils/buildRows.js';
import { EMPTY_REVIEW_FILTERS } from '../../../domains/people-access/utils/reviewFilters.js';
import { partitionRows, validateRows } from '../../../domains/people-access/utils/validateRows.js';

const EMPTY_REVIEW = Object.freeze({ search: '', filters: EMPTY_REVIEW_FILTERS, selection: [] });
const NOTHING_UNLISTED = Object.freeze({ unchanged: 0, removed: 0 });
const NO_INVITES = Object.freeze({ mode: INVITE_MODE.NONE, selectedIds: [], reinviteExisting: false, note: '' });

/**
 * Shape of the wizard state (see CT-5634): `{ step, file, rawRows, mapping, rows, review,
 * invite }`, plus `catalog` (roles, groups, existing members and domain the rows were checked
 * against) and `notListed` (rows left out of the list: repeated, unchanged, removed). It lives only while `members/import`
 * is mounted and nothing is sent to a service before step 4.
 */
export const initialImportWizardState = Object.freeze({
  step: IMPORT_STEPS.UPLOAD,
  file: null,
  rawRows: [],
  mapping: {},
  rows: [],
  catalog: null,
  notListed: NOTHING_UNLISTED,
  review: EMPTY_REVIEW,
  invite: NO_INVITES,
});

/** Everything derived from the file: cleared whenever the file changes. */
const withFile = (state, { file, rawRows }) => ({
  ...initialImportWizardState,
  step: state.step,
  file,
  rawRows,
});

/** Opening the match step automaps once; an existing mapping is never overwritten. */
function enterStep(state, step) {
  const needsAutomap =
    step === IMPORT_STEPS.MATCH && state.file && Object.keys(state.mapping).length === 0;
  return {
    ...state,
    step,
    mapping: needsAutomap ? automapColumns(state.file.headers) : state.mapping,
  };
}

/**
 * One target per column: the column releases the target it held, and a taken target moves
 * over. The rows derived from the old mapping are dropped (they are rebuilt on Next).
 */
function setMapping(state, { column, target }) {
  const mapping = Object.fromEntries(
    Object.entries(state.mapping).map(([name, mapped]) => [
      name,
      target !== IMPORT_TARGET.SKIP && mapped === target ? IMPORT_TARGET.SKIP : mapped,
    ]),
  );
  return {
    ...state,
    mapping: { ...mapping, [column]: target },
    rows: [],
    // No catalog means "the rows are not built for this mapping": Next builds them again.
    catalog: null,
    notListed: NOTHING_UNLISTED,
    review: EMPTY_REVIEW,
  };
}

function buildReviewRows(state, { catalog }) {
  const { listed, notListed } = partitionRows(
    buildRows({ rawRows: state.rawRows, headers: state.file.headers, mapping: state.mapping }),
    catalog,
  );
  return { ...state, rows: listed, notListed: { ...notListed, removed: 0 }, catalog, review: EMPTY_REVIEW };
}

const revalidated = (state, rows) => ({ ...state, rows: validateRows(rows, state.catalog) });

const mapRows = (rows, rowNumbers, change) =>
  rows.map((row) => (rowNumbers.includes(row.rowNumber) ? change(row) : row));

/** A different access has to be confirmed again: an Admin confirmed before is not confirmed after. */
const withAccess = (row, access) => ({ ...row, access, adminConfirmed: false });

/** Members that already exist keep the access they have: imports never change it. */
const assignAccess = (state, rowNumbers, access) => ({
  ...state,
  rows: mapRows(state.rows, rowNumbers, (row) =>
    row.status === ROW_STATUS.UPDATE ? row : withAccess(row, normalizeAccess(access)),
  ),
});

const applyFromFile = (state, { rowNumbers }) => ({
  ...state,
  rows: mapRows(state.rows, rowNumbers, (row) =>
    row.status === ROW_STATUS.UPDATE ? row : withAccess(row, accessFromFile(row, state.catalog)),
  ),
});

const restoreAccess = (state, previous) => {
  const byRow = new Map(previous.map(({ rowNumber, access }) => [rowNumber, access]));
  return {
    ...state,
    rows: state.rows.map((row) => (byRow.has(row.rowNumber) ? withAccess(row, byRow.get(row.rowNumber)) : row)),
  };
};

/**
 * Same rules as the first build, for a list that changed by hand (Fix, Remove): rows are
 * re-validated, a repeated email or a member with nothing to update leaves the list, a row that
 * now matches an existing member keeps that member's own access, and the selection follows.
 */
function repartition(state, rows) {
  const { listed, notListed } = partitionRows(rows, state.catalog);
  const kept = new Set(listed.map((row) => row.rowNumber));
  return {
    ...state,
    rows: listed.map((row) => (row.status === ROW_STATUS.UPDATE && row.access ? { ...row, access: null } : row)),
    notListed: {
      ...state.notListed,
      unchanged: state.notListed.unchanged + notListed.unchanged,
    },
    review: { ...state.review, selection: state.review.selection.filter((n) => kept.has(n)) },
  };
}

/** Save of the Fix dialog: the only moment a row is re-validated. */
function fixRow(state, { rowNumber, person, accessFromFile: fileAccess, assignNow }) {
  const rows = state.rows.map((row) => {
    if (row.rowNumber !== rowNumber) return row;
    const merged = { ...row.person, ...person };
    // Another person (a different email or name): what was decided about the old one does not apply.
    const sameIdentity =
      merged.email === row.person.email &&
      merged.first_name === row.person.first_name &&
      merged.last_name === row.person.last_name;
    const next = {
      ...row,
      person: merged,
      accessFromFile: fileAccess ?? row.accessFromFile,
      ...(sameIdentity ? {} : { resolution: null, adminConfirmed: false, domainConfirmed: false }),
    };
    return assignNow && row.status !== ROW_STATUS.UPDATE
      ? withAccess(next, accessFromFile(next, state.catalog))
      : next;
  });
  return repartition(state, rows);
}

function removeRows(state, rowNumbers) {
  const rows = state.rows.filter((row) => !rowNumbers.includes(row.rowNumber));
  const next = repartition(state, rows);
  // Rows the admin took off by hand count as "won't be added" in the Check step summary.
  const removed = state.rows.length - rows.length;
  return { ...next, notListed: { ...next.notListed, removed: next.notListed.removed + removed } };
}

/** Undo of Remove from list: the rows come back in their place and no longer count as removed. */
function restoreRows(state, rows) {
  // A second Undo, or a snapshot of rows that are listed again, must not add them twice.
  const listed = new Set(state.rows.map((row) => row.rowNumber));
  const back = rows.filter((row) => !listed.has(row.rowNumber));
  if (back.length === 0) return state;
  const next = repartition(
    state,
    [...state.rows, ...back].sort((a, b) => a.rowNumber - b.rowNumber),
  );
  return { ...next, notListed: { ...next.notListed, removed: Math.max(0, next.notListed.removed - back.length) } };
}

const emailKey = (row) => String(row?.person?.email ?? '').trim().toLowerCase();

/** Same email is one person: every other row with that email leaves the list. */
function keepOnlySameEmail(state, keepRowNumber) {
  const email = emailKey(state.rows.find((row) => row.rowNumber === keepRowNumber));
  if (!email) return state;
  const drop = state.rows
    .filter((row) => row.rowNumber !== keepRowNumber && emailKey(row) === email)
    .map((row) => row.rowNumber);
  return drop.length > 0 ? removeRows(state, drop) : state;
}

/** Step 4, "Before you add them": what the user decided about a possible duplicate. */
function resolveDuplicate(state, { rowNumber, resolution }) {
  if (resolution === DUPLICATE_RESOLUTION.KEEP_THIS) return keepOnlySameEmail(state, rowNumber);
  if (resolution === DUPLICATE_RESOLUTION.KEEP_OTHER) {
    const sameEmail = state.rows
      .find((row) => row.rowNumber === rowNumber)
      ?.issues.find((issue) => issue.sameEmail);
    // "Keep row N only" on a repeated email drops every other copy, not just this card's row.
    if (sameEmail) return keepOnlySameEmail(state, sameEmail.otherRowNumber);
    return removeRows(state, [rowNumber]);
  }
  const rows = state.rows.map((row) => {
    if (row.rowNumber !== rowNumber) return row;
    const match = row.issues.find((issue) => issue.kind === DUPLICATE_KIND.MEMBER);
    return {
      ...row,
      resolution,
      existingMemberId: resolution === DUPLICATE_RESOLUTION.USE_EXISTING ? (match?.memberId ?? null) : row.existingMemberId,
    };
  });
  return repartition(state, rows);
}

/** Confirm the Admin, or change the person to Member. */
function resolveAdmin(state, { rowNumber, choice }) {
  const rows = mapRows(state.rows, [rowNumber], (row) => ({
    ...row,
    // Only Confirm Admin confirms; Change to Member leaves nothing confirmed for a later Admin.
    adminConfirmed: choice === ADMIN_CHOICE.CONFIRM,
    access: choice === ADMIN_CHOICE.MEMBER ? normalizeAccess({ ...row.access, user_type: 'member' }) : row.access,
  }));
  return { ...state, rows };
}

/** Keep the person with an outside-domain email, take them off the list, or undo a Keep. */
function resolveOutsideDomain(state, { rowNumber, choice }) {
  if (choice === DOMAIN_CHOICE.REMOVE) return removeRows(state, [rowNumber]);
  const domainConfirmed = choice === DOMAIN_CHOICE.KEEP;
  return { ...state, rows: mapRows(state.rows, [rowNumber], (row) => ({ ...row, domainConfirmed })) };
}

const setReview = (state, change) => ({ ...state, review: { ...state.review, ...change } });

/**
 * Pure reducer of the wizard. Stories add actions here, they never create their own state.
 *
 * @param {typeof initialImportWizardState} state
 * @param {{ type: string, payload?: unknown }} action
 */
export function importWizardReducer(state, action) {
  const { payload } = action;
  switch (action.type) {
    // A first load and a replace are the same transition: everything derived from the previous
    // file (mapping, rows, review, invite) is dropped. `FILE_REPLACED` stays as an explicit alias
    // for the later stories of the wizard that dispatch it by name.
    case IMPORT_ACTIONS.FILE_LOADED:
    case IMPORT_ACTIONS.FILE_REPLACED:
      return withFile(state, payload);
    case IMPORT_ACTIONS.SET_STEP:
      return IMPORT_STEP_ORDER.includes(payload) ? enterStep(state, payload) : state;
    case IMPORT_ACTIONS.SET_MAPPING:
      return setMapping(state, payload);
    case IMPORT_ACTIONS.BUILD_ROWS:
      return buildReviewRows(state, payload);
    case IMPORT_ACTIONS.SET_ROW_ACCESS:
      return assignAccess(state, payload.rowNumbers, payload.access);
    case IMPORT_ACTIONS.APPLY_ACCESS_FROM_FILE:
      return applyFromFile(state, payload);
    case IMPORT_ACTIONS.RESTORE_ROW_ACCESS:
      return restoreAccess(state, payload);
    case IMPORT_ACTIONS.FIX_ROW:
      return fixRow(state, payload);
    case IMPORT_ACTIONS.REMOVE_ROWS:
      return removeRows(state, payload);
    case IMPORT_ACTIONS.RESTORE_ROWS:
      return restoreRows(state, payload);
    case IMPORT_ACTIONS.SET_REVIEW_SEARCH:
      return setReview(state, { search: payload });
    case IMPORT_ACTIONS.SET_REVIEW_FILTERS:
      return setReview(state, { filters: { ...state.review.filters, ...payload } });
    case IMPORT_ACTIONS.SET_REVIEW_SELECTION:
      return setReview(state, { selection: payload });
    case IMPORT_ACTIONS.RESOLVE_DUPLICATE:
      return resolveDuplicate(state, payload);
    case IMPORT_ACTIONS.RESOLVE_ADMIN:
      return resolveAdmin(state, payload);
    case IMPORT_ACTIONS.RESOLVE_OUTSIDE_DOMAIN:
      return resolveOutsideDomain(state, payload);
    case IMPORT_ACTIONS.SET_INVITE:
      return { ...state, invite: { ...state.invite, ...payload } };
    case IMPORT_ACTIONS.RESET:
      return initialImportWizardState;
    default:
      return state;
  }
}
